// billing-plugin/common/schemas/collections/subscriptionBillings.js
import { z } from "zod";

/**
 * The live, mutable version of a BillingLineItem associated with a specific subscription.
 */
export const ActiveBillingLineItemSchema = z.object({
    featureKey: z.string(),
    name: z.string(),
    billerConfig: z.record(z.any()).optional(),
    
    oneTimeFee: z.number().int().optional().nullable(),
    recurringFee: z.number().int().optional().nullable(),
    
    freeUnits: z.number().int(),
    maxUnits: z.number().int().positive().optional().nullable(),
    
    ratesheetId: z.string().optional().nullable().describe("ID of a custom rate sheet for dynamic pricing."),
    
    usedUnits: z.number().default(0).describe("Current usage in this period."),
    lastRefreshedAt: z.coerce.date().describe("Timestamp of the last usage reset."),
    
    overrideSource: z.string().optional().describe("ID of the ServicePack that provided this item, if any."),
});
/**
 * A snapshot of an applied discount on a subscription.
 */
export const ActiveDiscountSchema = z.object({
    code: z.string(),
    discountId: z.string(),
    appliedAt: z.coerce.date(),
    // Snapshot of key properties at time of application
    discountType: z.enum(['percentage', 'fixed_amount']),
    value: z.number().positive(),
    duration: z.enum(['once', 'forever', 'repeating']),
    durationInMonths: z.number().int().optional(),
    monthsApplied: z.number().int().default(0),
});

/**
 * State of a subscription in the dunning (payment recovery) process.
 */
export const DunningStateSchema = z.object({
    level: z.number().int().default(0).describe("0=not dunning, 1=first attempt, etc."),
    lastAttemptAt: z.coerce.date().optional().nullable(),
    nextAttemptAt: z.coerce.date().optional().nullable(),
});

/**
 * The primary billing record for a user or an entire subscription.
 */
const SubscriptionBillingSchema = z.object({
    _id: z.string().optional().describe("MongoDB ObjectId"),
    targetType: z.enum(['subscription', 'user']),
    targetId: z.string().describe("The subscriptionId or userId this billing profile belongs to."),
    
    planId: z.string(),
    planSnapshot: z.any().describe("A full snapshot of the BillingPlan object at the time of subscription."),
    
    status: z.enum(['active', 'trial', 'past_due', 'cancelled']).default('active'),
    
    balance: z.number().int().default(0).describe("Current account balance in cents. Can be negative."),
    creditLimit: z.number().int().default(0).describe("How far the balance can go negative before services are suspended, in cents."),
    
    activeLineItems: z.array(ActiveBillingLineItemSchema),
    activeDiscounts: z.array(ActiveDiscountSchema).optional().default([]),
    dunningState: DunningStateSchema.optional().nullable(),
    
    subscribedAt: z.coerce.date(),
    nextBillingDate: z.coerce.date(),
    cancelledAt: z.coerce.date().optional().nullable(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});

export default SubscriptionBillingSchema;